import { useEffect, useState } from "react";
import { downloadReport, gallery } from "../../asset";
import FileUpload from "../../components/common/FileUpload";
import PageHeader from "../../components/PageHeader";
import galleryApis from "../../api/galleryApis";
import { GalleryMedia } from "../../types/galleryType";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import AttachmentViewerModal from "../../components/modal/AttachmentViewModal";
import { FiX } from "react-icons/fi";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import SubButton from "../../components/SubButtons";
import { toast } from "sonner";
import JSZip from "jszip";
import { saveAs } from "file-saver";

// Helper: group images by Month + Year
const groupByMonth = (media: GalleryMedia[]) => {
  return media.reduce<Record<string, GalleryMedia[]>>((acc, item) => {
    const date = new Date(item.createdAt);
    const key = date.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });

    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
};

export default function MediaGallery() {
  const [images, setImages] = useState<GalleryMedia[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { confirm, modal } = useConfirmToast();
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [activeMonth, setActiveMonth] = useState<string | null>(null);

  const branchCode = useSelector(
    (state: RootState) => state.access.schoolBranch,
  );

  useEffect(() => {
    loadGallery();
  }, [branchCode]);

  const loadGallery = async () => {
    const res = await galleryApis.getGallery(
      branchCode.schoolBranchCode,
      branchCode.schoolCode,
      0,
      500,
    );
    setImages(res.results);
  };

  const handleImageUpload = async (urls: string[]) => {
    try {
      await Promise.all(
        urls.map((url) =>
          galleryApis.postGalleryImage({
            mediaUrl: url,
            mediaType: "IMAGE",
            branchCode: branchCode.schoolBranchCode,
          }),
        ),
      );

      toast.success("Images uploaded successfully");
      loadGallery();
    } catch (err) {
      toast.error("Upload failed");
      console.error(err);
    }
  };

  const handleDelete = async (mediaId: number) => {
    const isConfirmed = await confirm(
      "Are you sure you want to delete this image?",
    );

    if (!isConfirmed) return;

    try {
      await galleryApis.deleteImage(mediaId);
      loadGallery();
    } catch (err) {
      console.error(err);
    }
  };

  const groupedImages = groupByMonth(images);

  const handleDownloadZip = async () => {
    if (selectedIds.length === 0) {
      toast.error("No images selected");
      return;
    }

    try {
      const zip = new JSZip();

      const selectedImages = images.filter((img) =>
        selectedIds.includes(img.mediaId),
      );

      // loop + fetch each image
      await Promise.all(
        selectedImages.map(async (img, index) => {
          const response = await fetch(img.mediaUrl);
          const blob = await response.blob();

          const filename =
            img.mediaUrl.split("/").pop() || `image-${index + 1}.jpg`;

          zip.file(filename, blob);
        }),
      );

      // generate zip
      const content = await zip.generateAsync({ type: "blob" });

      // download
      saveAs(content, `${activeMonth ?? "gallery-images"}.zip`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to download images");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between gap-5">
        <PageHeader />

        <FileUpload<true>
          multiple={true}
          onUploadComplete={handleImageUpload}
          showDropzone={false}
          buttonText="Upload Media"
          className="w-fit"
        />
      </div>

      {images.length === 0 ? (
        <div className="flex flex-col h-[420px] bg-white p-4 rounded-lg gap-16 justify-center items-center">
          <img src={gallery} alt="no images" />
          <FileUpload<true>
            onUploadComplete={handleImageUpload}
            showDropzone={false}
            multiple={true}
            buttonText="Upload Media"
            className="w-fit"
          />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {Object.entries(groupedImages).map(([month, items]) => (
            <div
              key={month}
              className="flex flex-col gap-3 bg-white rounded-lg p-4"
            >
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold text-neutral-500">
                  {month}
                </h3>
                <div className="flex gap-4">
                  {!selectionMode && (
                    <SubButton
                      onClick={() => {
                        setSelectionMode(true);
                        setSelectedIds([]);
                        setActiveMonth(month);
                      }}
                      color="bg-warning-600"
                      text={"Select"}
                    />
                  )}

                  {selectionMode && activeMonth === month && (
                    <SubButton
                      onClick={() => {
                        setSelectionMode(false);
                        setActiveMonth(null);
                        setSelectedIds([]);
                      }}
                      color="bg-error-600"
                      text="Cancel"
                    />
                  )}

                  {selectionMode &&
                    activeMonth === month &&
                    selectedIds.length > 0 && (
                      <SubButton
                        onClick={handleDownloadZip}
                        color="bg-success-600"
                        text={`Download (${selectedIds.length})`}
                        icon={downloadReport}
                      />
                    )}
                  {selectionMode && activeMonth === month && (
                    <SubButton
                      onClick={() => {
                        setActiveMonth(month);
                        setSelectedIds(items.map((img) => img.mediaId));
                      }}
                      color="bg-primary-800"
                      text="Select All"
                    />
                  )}
                </div>
              </div>

              <div className="flex gap-1 flex-wrap  pb-2 custom-scrollbar">
                {items.map((img, index) => {
                  // const mod = index % 3;

                  // let sizeClass = "";
                  // if (mod === 0) {
                  //   sizeClass = "w-[220px] h-[150px]";
                  // } else if (mod === 1) {
                  //   sizeClass = "w-[220px] h-[170px]";
                  // } else {
                  //   sizeClass = "w-[220px] h-[190px]";
                  // }

                  return (
                    <div
                      key={img.mediaId}
                      onClick={() => {
                        if (!selectionMode) {
                          setSelectedImage(img.mediaUrl);
                        }
                      }}
                      className={`relative group w-[220px] h-[190px] shrink-0 rounded-lg overflow-hidden cursor-pointer 
                        ${""}
                        `}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(img.mediaId);
                        }}
                        className="
    absolute top-2 right-2 z-10
    bg-black/60 hover:bg-red-600
    text-white rounded-full p-1
    opacity-0 scale-90
    group-hover:opacity-100 group-hover:scale-100
    transition-all duration-200
  "
                      >
                        <FiX size={16} />
                      </button>
                      <img
                        src={img.mediaUrl}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      {selectionMode && activeMonth === month && (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(img.mediaId)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => {
                            if (activeMonth && activeMonth !== month) {
                              toast.error(
                                "You can only select images from one month at a time.",
                              );
                              return;
                            }

                            setSelectedIds((prev) => {
                              const isSelected = prev.includes(img.mediaId);

                              // first selection → lock month
                              if (!activeMonth && !isSelected) {
                                setActiveMonth(month);
                              }

                              const updated = isSelected
                                ? prev.filter((id) => id !== img.mediaId)
                                : [...prev, img.mediaId];

                              if (updated.length === 0) {
                                setActiveMonth(null);
                              }

                              return updated;
                            });
                          }}
                          className="absolute top-2 left-2 w-4 h-4 z-20"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal}

      {selectedImage && (
        <AttachmentViewerModal
          fileUrl={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </div>
  );
}
